import bindAllOfThis from 'src/utils/BindThisHelper';
import { TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import { EndGameScene } from './EndGameScene';
import { DinnerItem, Patron } from '../objects/DinnerItem';
export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;

    items: DinnerItem[];
    patrons: Patron[];

    round: "drink" | "entree" | "dessert";

    constructor() {
        super({
            key: "MainScene"
        });
        bindAllOfThis(this, MainScene.prototype);
    }

    preload(): void {
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/waiter_memory.json"));
        this.load.image('background', require('/src/assets/qci/backgrounds/ks_background.png'));
        this.load.atlas("assets", require("src/assets/qci/atlas/gameWaiterMemory.png"), require("src/assets/qci/atlas/gameWaiterMemory.json"));
    }

    initialize(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'map' });
        this.round = "drink";
        this.items = [];
        this.patrons = [];
    }

    setupRound() {
        this.input.enabled = false;

        // Reset patrons and match them with a desired item randomly
        this.patrons.forEach((p: Patron) => {
            p.served = false;
            var id = this.round === "drink" ? p.drinkId : this.round === "entree" ? p.entreeId : p.dessertId;
            p.desiredItem.setFrame("bubblefood" + id);
            p.desiredItem.setVisible(true);
            p.bubble.setVisible(true);
            p.desiredItem.y = p.bubble.y - 20;
            p.desiredItem.x = p.bubble.x + 10;
        });

        this.createTray();
        // Hide tray items
        this.items.forEach((i: DinnerItem) => {
            if (!i.served) {
                i.setVisible(false);
            }
        });

        // Wait then show tray items and hide patron's desired items
        setTimeout(() => {
            this.input.enabled = true;
            this.patrons.forEach((p: Patron) => {
                p.desiredItem.setVisible(false);
                p.bubble.setVisible(false);
            });
            this.items.forEach((i: DinnerItem) => i.setVisible(true));
        }, 2000);
    }

    create(): void {
        this.initialize();
        var background = this.add.image(0, 0, "background").setOrigin(0).setDepth(0);
        this.createPatronZones();
        this.registerEvents();
        this.setupRound();
    }

    createTray() {
        var ids = [];
        if (this.round === "drink") {
            this.patrons.forEach((p: Patron) => ids.push(p.drinkId));
        }
        else if (this.round === "entree") {
            this.patrons.forEach((p: Patron) => ids.push(p.entreeId));
        }
        else {
            this.patrons.forEach((p: Patron) => ids.push(p.dessertId));
        }

        ids = this.scrambleArray(ids);
        this.map.getObjectLayer("Tray").objects.forEach((ob: TiledGameObject) => {
            // Only create tray if on drink round (first round)
            if (ob.name === "tray" && this.round === "drink") {
                var tray = this.add.sprite(ob.x, ob.y, "assets", "tray").setOrigin(0);
            }
            else if (ob.type === "Item") {
                var item = new DinnerItem(this, ob, this.round, ids.pop());
                this.items.push(item);
            }
        });
    }

    createPatronZones() {
        this.map.getObjectLayer("Scene").objects.forEach((ob: TiledGameObject) => {
            var patron = new Patron(this, ob);
            patron.on('pointerdown', () => {
                // Ignore if this patron is already served or if their desired item is already showing
                if (patron.served || patron.desiredItem.visible) {
                    return;
                }

                this.showPatronItemReminder(patron);
            });

            ob = this.map.getObjectLayer("Plates").objects.find((ob: TiledGameObject) => ob.properties["id"] === patron.id) as TiledGameObject;
            patron.plate = new Phaser.Geom.Rectangle(ob.x, ob.y, ob.width, ob.height);

            this.patrons.push(patron);
        });
    }

    showPatronItemReminder(patron: Patron) {
        // Disable input and show this patrons item, hide all others
        this.input.enabled = false;
        patron.desiredItem.setVisible(true);
        patron.bubble.setVisible(true);
        this.patrons.forEach((p: Patron) => {
            if (p.id !== patron.id) {
                p.desiredItem.setVisible(false);
                p.bubble.setVisible(false);
            }
        });
        this.items.forEach((i: DinnerItem) => {
            if (!i.served) {
                i.setVisible(false);
            }
        });

        // After a second, hide this patrons item again and show tray items
        setTimeout(() => {
            patron.desiredItem.setVisible(false);
            patron.bubble.setVisible(false);
            this.items.forEach((i: DinnerItem) => i.setVisible(true));
            this.input.enabled = true;
        }, 600);
    }

    registerEvents(): void {
        this.input.dragDistanceThreshold = 10;

        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, item: DinnerItem) => {
            this.children.bringToTop(item);
            pointer.x = item.x;
            pointer.y = item.y;
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, item, dragX: number, dragY: number) => {
            item.x = pointer.x;
            item.y = pointer.y;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, item, dropped: boolean) => {
            if (!dropped) {
                // Put back
                item.x = item.input.dragStartX;
                item.y = item.input.dragStartY;
            }
        });

        this.input.on('drop', (pointer: Phaser.Input.Pointer, item: DinnerItem, patron: Patron) => {
            this.drop(item, patron);
        });
    }

    drop(item: DinnerItem, patron: Patron) {
        this.children.bringToTop(item);

        // They are locked into their home
        if (this.isCorrectMatch(item, patron)) {
            item.served = true;
            item.input.enabled = false;
            patron.served = true;
            item.putOnPlate(patron);
        }
        else {
            // Put back
            item.x = item.input.dragStartX;
            item.y = item.input.dragStartY;
            item.served = false;
        }

        this.isGameOver();
    }

    isCorrectMatch(item: DinnerItem, patron: Patron) {
        return this.round === "drink" && item.id === patron.drinkId ||
            this.round === "entree" && item.id === patron.entreeId ||
            this.round === "dessert" && item.id === patron.dessertId;
    }

    isGameOver(): boolean {
        // First check that all patrons have been served
        for (var patron of this.patrons) {
            if (!patron.served) {
                return false;
            }
        }

        // Now switch to the next round
        switch (this.round) {
            case this.round = "drink":
                this.round = "entree";
                break;
            case this.round = "entree":
                this.round = "dessert";
                break;
            case this.round = "dessert":
                //  Wait a second and then end the game
                setTimeout(() => {
                    this.scene.add("EndGameScene", EndGameScene, true);
                    this.scene.remove(this);
                }, 1000);
                return;
        }

        this.setupRound();
    }

    scrambleArray(array) {
        var j, x, i;
        for (i = array.length - 1; i > 0; i--) {
            j = Math.floor(Math.random() * (i + 1));
            x = array[i];
            array[i] = array[j];
            array[j] = x;
        }
        return array;
    }

}



// WEBPACK FOOTER //
// ./src/games/WaiterMemoryGame/scenes/MainScene.ts